import { useAppDispatch, useAppSelector } from "@/Hook/hooks";
import { getScreenConfig } from "@/Redux/Ducks/entryFormSlice";
import { EApprovalStatusType, ENotificationType, setWorkflowApproval, stopApprovalAction } from "@/Redux/Ducks/inboxSlice";
import { openInputAlert, showValidationMessage } from "@/UI/BaseComponents/Alert";
import AlignTimeAgo from "@/UI/BaseComponents/AlignTimeAgo";
import { constant } from "@/Utils/constants";
import BaseMethods from "AlignBaseUI/BaseMethods";
import { ControlTranslation, Icon } from "AlignComponents";
import { useRef } from "react";
import { Button, Card, Nav, Table } from "react-bootstrap";
import { FaCheckCircle, FaCloudDownloadAlt } from "react-icons/fa";
import { HiMail } from "react-icons/hi";
import { useHistory } from "react-router";

const ActiveApproval = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const approvalReasonRef = useRef<any>();
  const pathname = history.location.pathname;

  const inbox = useAppSelector((state) => ({
    inbox: state.Inbox.messages?.["/approval/active"]?.data || [],
    inboxLoaded: !state.Inbox.messages?.["/approval/active"]?.dataLoading,
    dataLoaded: state.Home.dataLoaded,
    currentUserId: state.User.user.username
  }));


  const user: any = useAppSelector((state) => state.User.user.name);

  const openMessage = (message: any) => {
    history.push({
      pathname: "approval/active",
      state: { activeMessage: message }
    });
  }

  const routeToScreen = (screenCode: string, transactionNo: string = "") => {
    dispatch(getScreenConfig({ processCode: screenCode, transactionNo: transactionNo }));
  };

  const setApproval = async (approvalResponse: EApprovalStatusType, messageObject: any) => {
    let reason = "";

    if (approvalResponse.isEqual(EApprovalStatusType.Hold, EApprovalStatusType.Reject)) {

      const reasonFieldConfig = {
        fieldid: "approvalcomment",
        fieldlabel: "Comment",
      };

      const preConfirm = () => {
        if (!approvalReasonRef.current.value) {
          showValidationMessage("Please fill the comment");
        }
        else {
          return approvalReasonRef.current.value;
        }
      }

      const res = await openInputAlert<ReturnType<typeof preConfirm>>({
        title: "Please enter comments",
        extraOption: {
          input: undefined,
          preConfirm,
          html:
            <div className="field-container" data-fieldid={reasonFieldConfig.fieldid}>
              <label id={`lb-${reasonFieldConfig.fieldid}`} htmlFor={`field-${reasonFieldConfig.fieldid}`} >
                {reasonFieldConfig.fieldlabel}
                <span>*</span>
              </label>
              <textarea
                ref={approvalReasonRef}
                className="field-textarea"
                rows={6} cols={35}
                id={`field-${reasonFieldConfig.fieldid}`}
              />
            </div>
        }
      });
      if (res.cancel)
        return;

      reason = res.value;
    }
    dispatch(setWorkflowApproval({
      approvalResponse, approvalprid: messageObject.refnoprid, reason, onSuccess: () => {
        dispatch(stopApprovalAction({ messagePrid: messageObject.prid, key: pathname }));
      }
    }));
  }


  return (
    <div className="widget active-approvals">
      <Card>
        <Card.Header>
          <Card.Title>
            <HiMail size={18} className="icon" />
            <ControlTranslation system={true} controlId="lblactiveapproval" />
          </Card.Title>
          <Nav.Link className="view-all ms-auto" onClick={() => history.push('approval/active')}>
            <ControlTranslation system={true} controlId="lblviewall" />
          </Nav.Link>
        </Card.Header>
        <Card.Body className="overflow-auto p-0 text-nowrap">
          {
            inbox.inboxLoaded ?
              inbox.inbox.length ?

                <Table hover>
                  <thead>
                    <tr className="position-sticky top-0">
                      <th><ControlTranslation system={true} controlId="lblrequester" /></th>
                      {/* <th>Type</th> */}
                      <th><ControlTranslation system={true} controlId="lblprocess" /></th>
                      <th><ControlTranslation system={true} controlId="lbldocumentno" /></th>
                      <th><ControlTranslation system={true} controlId="lblrequesteddate" /></th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {
                      inbox.inbox.map((x: any, idx: number) => (
                        <tr key={idx} onClick={(e: any) => { openMessage(x) }} role="button">
                          <td>
                            <div className="d-flex align-items-center mb-0">
                              <img src={BaseMethods.getUserPhoto(x.userphoto)} alt="" className="rounded-circle thumb-xs me-1" />
                              <div className="d-flex flex-column">
                                <p className="fw-bold m-0">
                                  {x.messageuserfullname}
                                </p>
                                <p className="subject">
                                  {x.subject}
                                </p>
                              </div>
                              <br />
                            </div>
                          </td>
                          <td>
                            {x.processstxt || "-"}
                          </td>
                          <td>
                            {x.parameters ? x.parameters.transactionno : "-"}
                            <Icon icon="ExternalLink" size="16" className="ms-2" onClick={(e) => { e.stopPropagation(); routeToScreen(x.parameters.processcode, x.parameters.transactionno) }} />
                          </td>
                          <td>
                            {BaseMethods.convertDateToCompanyFormat(BaseMethods.convertUTCDateToLocal(x.credate).toString(), constant.DefaultDateFormat)}
                            <br />
                            <AlignTimeAgo date={BaseMethods.convertUTCDateToLocal(x.credate).toString()} />
                          </td>
                          <td>
                            <div className="d-flex">
                              {
                                x.tousername == inbox.currentUserId &&
                                  x.notificationtype == ENotificationType.Approval &&
                                  x.actionable ?
                                  <>
                                    <Button variant="primary" onClick={(e) => { e.stopPropagation(); setApproval(EApprovalStatusType.Approve, x) }}>
                                      <ControlTranslation system={true} controlId="btnapprove" />
                                    </Button>
                                    <Button variant="danger" className="ms-2" onClick={(e) => { e.stopPropagation(); setApproval(EApprovalStatusType.Reject, x) }}>
                                      <ControlTranslation system={true} controlId="btnreject" />
                                    </Button>
                                    <Button variant="warning" className="ms-2" onClick={(e) => { e.stopPropagation(); setApproval(EApprovalStatusType.Hold, x) }}>
                                      <ControlTranslation system={true} controlId="btnhold" />
                                    </Button>
                                  </>
                                  :
                                  null
                              }
                            </div>
                          </td>
                        </tr>
                      ))
                    }
                  </tbody>
                </Table>
                :
                <div className="text-group">
                  <FaCheckCircle size="40" />
                  <p className="mb-0"><ControlTranslation system={true} controlId="lblnothingfound" /></p>
                  <span><ControlTranslation system={true} controlId="lblyouhavenomailtoshow" /></span>
                </div>
              :
              <div className="text-group">
                <FaCloudDownloadAlt size="40" />
                <h6>
                  <ControlTranslation system={true} controlId="lblloading" />
                </h6>
                <span>
                  <ControlTranslation system={true} controlId="lblwait" />
                </span>
              </div>
          }
        </Card.Body>
      </Card>
    </div >
  )
};

export default ActiveApproval;